import { useCallback, useEffect, useMemo } from 'react'

import { CreatorPermission } from '@api'
import { getCurrency } from '@common/utils/currency'
import { format } from 'date-fns/format'
import { useUnit } from 'effector-react'
import { useNavigate } from 'react-router'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Page } from '@/components/layout/page/page.tsx'
import { useDateRangeContext } from '@/components/provider/date-range-provider.tsx'
import { CalendarRangeDatePicker } from '@/components/ui/calendar/calendar-range-date-picker.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { WithRefresher } from '@/components/ui/with-refresher'
import { CHART_LINE_COLORS } from '@/consts/colors'
import { $account } from '@/models/account.ts'
import { $creators, $isCreatorProcessCompleted } from '@/models/creators.ts'
import {
  $allChatters,
  $refreshAvailable,
  findLeaderboardByDateFx,
} from '@/models/statistics/leaderboard.ts'
import { useAccount } from '@/modules/accounts/hooks/useAccounts'
import { CreatorFeatures } from '@/modules/accounts/types'
import { useCreators } from '@/modules/creators/hooks/useCreators'
import { isActiveCreator } from '@/modules/creators/utils.ts'
import { useCreatorOverviewComparison } from '@/modules/overview/hook/useCreatorOverviewComparison'
import { useCreatorOverviewSelect } from '@/modules/overview/hook/useCreatorOverviewSelect'
import { CategoryChart } from '@/modules/overview/ui/CategoryChart/CatergoryChart'
import { CreatorCombobox } from '@/modules/overview/ui/CreatorCombobox'
import { DetailedComparison } from '@/modules/overview/ui/DetailedComparison'
import { getEmptyDetailedComparisonData } from '@/modules/overview/ui/DetailedComparison/utils'
import { TotalChart } from '@/modules/overview/ui/TotalChart'
import { ChatterLeaderboardTable } from '@/modules/overview/ui/leaderboard/chatter-leaderboard-table.tsx'
import { getEmptyTrackingData } from '@/modules/overview/ui/leaderboard/utils'
import {
  mapNewSubscriptionsData,
  mapRevenueData,
  mapSubscriptionsData,
  mapTotalPurchasesData,
} from '@/modules/overview/utils'
import { EditTableWrapper } from '@/modules/sales-editor/wrappers/edit-table-wrapper'
import { LineChartEditorWrapper } from '@/modules/sales-editor/wrappers/line-chart-editor-wrapper'
import { useMaxDateRange } from '@/modules/statistics/onlyfans/hook/useMaxDateRange.ts'
import { AppRoutes } from '@/utils/constants.ts'
import {
  formatNumberWithCommas,
  hasPermissionForAnyCreator,
} from '@/utils/helpers'

export const OverviewView = () => {
  const account = useUnit($account)
  const { userSettings } = useAccount()
  const creators = useUnit($creators)
  const refreshAvailable = useUnit($refreshAvailable)
  const isCreatorProcessCompleted = useUnit($isCreatorProcessCompleted)

  const {
    selection: {
      allSelect,
      allRemove,
      addSelect,
      removeSelect,
      defaultSelectedCreators,
      selectableCreators,
      selectedCreators,
    },
  } = useCreators()
  const maxRangeQuery = useMaxDateRange(null)
  const { date, setDate, isOneDay } = useDateRangeContext()
  const {
    overviewData,
    isFetching,
    refetch: refetchCreatorOverviewSelect,
  } = useCreatorOverviewSelect(date, defaultSelectedCreators)
  const {
    data: comparisonData,
    isFetching: comparisonDataLoading,
    refetch: refetchOverviewComparison,
  } = useCreatorOverviewComparison(date, selectedCreators)

  const {
    totalTurnover,
    totalNewSubs,
    totalPurchases,
    subscriptions,
    revenue,
  } = useMemo(
    () => ({
      totalTurnover: mapRevenueData(overviewData, undefined, true, isOneDay),
      totalNewSubs: mapNewSubscriptionsData(
        overviewData,
        undefined,
        true,
        isOneDay,
      ),
      totalPurchases: mapTotalPurchasesData(overviewData, true, isOneDay),
      subscriptions: mapSubscriptionsData(overviewData, isOneDay),
      revenue: mapRevenueData(overviewData, true, false, isOneDay),
    }),
    [isOneDay, overviewData],
  )

  const allChatters = useUnit($allChatters)
  const chatterDataFetching = useUnit(findLeaderboardByDateFx.pending)

  const canSeeStats = useMemo(
    () =>
      hasPermissionForAnyCreator(
        account,
        creators,
        CreatorFeatures.stats,
        CreatorPermission.SEE_STATS,
        isCreatorProcessCompleted,
      ),
    [creators, account, isCreatorProcessCompleted],
  )
  const canSeeChatterLeaderboard = useMemo(
    () =>
      hasPermissionForAnyCreator(
        account,
        selectedCreators,
        CreatorFeatures.tracking,
        CreatorPermission.SEE_CHATTER_TRACKING,
        isCreatorProcessCompleted,
        false,
      ),
    [selectedCreators, account, isCreatorProcessCompleted],
  )

  const shouldShowCents = userSettings?.showCents

  const greeting = () => {
    const now = new Date()
    const currentYear = now.getUTCFullYear()
    const currentTime = now.getTime()

    // Dec 21 08:00 UTC
    const dec21Start = Date.UTC(currentYear, 11, 21, 8, 0, 0)
    // Dec 24 08:00 UTC
    const dec24Start = Date.UTC(currentYear, 11, 24, 8, 0, 0)
    // Dec 27 00:00 UTC
    const dec27Start = Date.UTC(currentYear, 11, 27, 0, 0, 0)
    // Jan 1 00:00 UTC (next year)
    const jan1Start = Date.UTC(currentYear + 1, 0, 1, 0, 0, 0)

    // Check holiday periods
    if (currentTime >= dec21Start && currentTime < dec24Start) {
      return 'Happy Holidays 🎄'
    } else if (currentTime >= dec24Start && currentTime < dec27Start) {
      return 'Merry Christmas 🎁✨'
    } else if (currentTime >= dec27Start && currentTime < jan1Start) {
      return 'Happy Holidays 🎄'
    }

    // Default time-based greeting
    const hours = now.getHours()
    let greet = 'Good '

    if (hours >= 6 && hours < 12) greet += 'Morning'
    else if (hours >= 12 && hours <= 17) greet += 'Afternoon'
    else if (hours >= 17 || hours <= 6) greet += 'Evening'

    return greet
  }

  const refetchLeaderboardData = useCallback(() => {
    findLeaderboardByDateFx({
      from: date?.from,
      to: date?.to,
      creatorIds: selectedCreators
        .filter(
          (creator) =>
            creator.permissions.includes(
              CreatorPermission.SEE_CHATTER_TRACKING,
            ) &&
            creator.features.includes(CreatorFeatures.tracking) &&
            isActiveCreator(creator),
        )
        .map((creator) => creator.id),
    })
  }, [date, selectedCreators])

  const handleRefresh = useCallback(() => {
    refetchCreatorOverviewSelect()
    refetchOverviewComparison()
    refetchLeaderboardData()
  }, [
    refetchCreatorOverviewSelect,
    refetchLeaderboardData,
    refetchOverviewComparison,
  ])

  // Navigating away to creators if no permissions
  const navigate = useNavigate()

  useEffect(() => {
    if (!canSeeStats && !canSeeChatterLeaderboard) {
      navigate(AppRoutes.Creators.List.asUrl())
    }
  }, [navigate, canSeeChatterLeaderboard, canSeeStats])

  useEffect(() => {
    if (!!selectedCreators.length && !allChatters && !chatterDataFetching) {
      refetchLeaderboardData()
    }
  }, [
    selectedCreators.length,
    allChatters,
    chatterDataFetching,
    refetchLeaderboardData,
  ])

  // Refetch leaderboard data when date changes
  useEffect(() => {
    if (canSeeChatterLeaderboard && selectedCreators.length) {
      refetchLeaderboardData()
    }
  }, [
    date,
    canSeeChatterLeaderboard,
    selectedCreators.length,
    refetchLeaderboardData,
  ])

  const dateRange = `${format(date.from ?? new Date(), 'yyyy-MM-dd')}-${format(date.to ?? new Date(), 'yyyy-MM-dd')}`

  return (
    <Page
      title={`${greeting()}${account ? `, ${account?.name?.trim()}` : ''}!`}
      headerOptions={
        <>
          <CreatorCombobox
            selectable={selectableCreators}
            selected={selectedCreators}
            onAllSelect={allSelect}
            onAllRemove={allRemove}
            onAddSelect={addSelect}
            onRemoveSelect={removeSelect}
          />
          <WithRefresher
            onRefresh={handleRefresh}
            isDisabled={!refreshAvailable}
            isFetching={
              (canSeeStats && (isFetching || comparisonDataLoading)) ||
              (canSeeChatterLeaderboard && chatterDataFetching)
            }
          >
            <CalendarRangeDatePicker
              date={date}
              setDate={setDate}
              maxDateRange={maxRangeQuery.data}
              dateNameSuffix={
                overviewData?.timezoneOffset
                  ? `(${overviewData?.timezoneOffset})`
                  : undefined
              }
            />
          </WithRefresher>
        </>
      }
    >
      <div className="grid gap-4 sm:gap-8">
        {canSeeStats && (
          <div className="grid grid-flow-row grid-cols-1 gap-4 sm:gap-8 xl:grid-cols-2">
            <div className="grid gap-4 sm:gap-8 md:grid-flow-col md:grid-cols-2">
              <LineChartEditorWrapper
                data={totalTurnover}
                totalValue={overviewData?.total.totalRevenue || 0}
                entityName="turnover"
                dateRange={dateRange}
                chartLineColor={CHART_LINE_COLORS.CYAN}
              >
                {({ data, totalValue }) => (
                  <TotalChart
                    title={
                      <p className="flex items-center gap-1">
                        Total Turnover{' '}
                        <Tooltip content="Shows the combined earnings from all creators during the selected timeframe. Includes all revenue sources from Subscriptions, Messages and Tips.">
                          <InfoIcon />
                        </Tooltip>
                      </p>
                    }
                    totalValue={totalValue}
                    formatTotalValue={(value) =>
                      getCurrency(value, shouldShowCents ? 2 : 0)
                    }
                    data={data}
                    isFetching={isFetching}
                    isCurrency
                    showCents={shouldShowCents}
                    testId="total-turnover"
                  />
                )}
              </LineChartEditorWrapper>

              <div className="grid gap-4 sm:grid-flow-col sm:gap-8 md:grid-flow-row">
                <LineChartEditorWrapper
                  data={totalNewSubs}
                  entityName="newSubs"
                  dateRange={dateRange}
                  totalValue={overviewData?.total.newSubscriptions || 0}
                  chartLineColor={CHART_LINE_COLORS.CYAN}
                >
                  {({ data, totalValue }) => (
                    <TotalChart
                      title={
                        <p className="flex items-center gap-1">
                          Total New Subs{' '}
                          <Tooltip content="Shows the total number of new subscribers gained within the selected timeframe. Only includes first-time subscriptions from fans.">
                            <InfoIcon />
                          </Tooltip>
                        </p>
                      }
                      totalValue={totalValue}
                      formatTotalValue={(value) =>
                        formatNumberWithCommas(Number(value))
                      }
                      variant="small"
                      data={data}
                      isFetching={isFetching}
                      testId="total-new-subs"
                    />
                  )}
                </LineChartEditorWrapper>
                <LineChartEditorWrapper
                  data={totalPurchases}
                  entityName="purchases"
                  dateRange={dateRange}
                  totalValue={overviewData?.total.totalPurchases || 0}
                  chartLineColor={CHART_LINE_COLORS.CYAN}
                >
                  {({ data, totalValue }) => (
                    <TotalChart
                      title={
                        <p className="flex items-center gap-1">
                          Total Purchases{' '}
                          <Tooltip content="Shows the total number of purchases made during the selected timeframe, including media unlocks, tips, and other paid transactions.">
                            <InfoIcon />
                          </Tooltip>
                        </p>
                      }
                      totalValue={totalValue}
                      formatTotalValue={(value) =>
                        formatNumberWithCommas(Number(value))
                      }
                      variant="small"
                      data={data}
                      isFetching={isFetching}
                      testId="total-purchases"
                    />
                  )}
                </LineChartEditorWrapper>
              </div>
            </div>

            <CategoryChart
              subscriptionsRevenue={
                overviewData?.total.totalSubscriptionsRevenue || 0
              }
              messagesRevenue={overviewData?.total.messageRevenue || 0}
              tipsRevenue={overviewData?.total.tipsRevenue || 0}
              isFetching={isFetching}
              showCents={shouldShowCents}
              dateRange={dateRange}
            />
          </div>
        )}

        {canSeeStats && (
          <div className="grid grid-flow-row grid-cols-1 gap-4 sm:gap-8 md:grid-cols-2">
            <LineChartEditorWrapper
              data={subscriptions}
              entityName="subscriptions"
              dateRange={dateRange}
              totalValue={overviewData?.total.newSubscriptions || 0}
              chartLineColor={CHART_LINE_COLORS.BLUE}
            >
              {({ data, totalValue }) => (
                <TotalChart
                  title={
                    <p className="flex items-center gap-1">
                      Subscriptions{' '}
                      <Tooltip content="Shows the total number of active and newly acquired subscriptions within the selected timeframe, providing insight into subscriber growth trends.">
                        <InfoIcon />
                      </Tooltip>
                    </p>
                  }
                  totalValue={totalValue}
                  formatTotalValue={(value) =>
                    formatNumberWithCommas(Number(value))
                  }
                  variant="big"
                  data={data}
                  isFetching={isFetching}
                  testId="subscriptions"
                />
              )}
            </LineChartEditorWrapper>
            <LineChartEditorWrapper
              data={revenue}
              entityName="revenue"
              dateRange={dateRange}
              totalValue={overviewData?.total.totalRevenue || 0}
              chartLineColor={CHART_LINE_COLORS.BLUE}
            >
              {({ data, totalValue }) => (
                <TotalChart
                  title={
                    <p className="flex items-center gap-1">
                      Revenue{' '}
                      <Tooltip content="Shows the combined revenue from all subscriptions, messages and tips also displayed in a graphical view.">
                        <InfoIcon />
                      </Tooltip>
                    </p>
                  }
                  totalValue={totalValue}
                  formatTotalValue={(value) =>
                    getCurrency(value, shouldShowCents ? 2 : 0)
                  }
                  variant="big"
                  data={data}
                  isFetching={isFetching}
                  isCurrency
                  showCents={shouldShowCents}
                  testId="revenue"
                />
              )}
            </LineChartEditorWrapper>
          </div>
        )}

        {canSeeStats && (
          <EditTableWrapper
            data={comparisonData}
            getEmptyRow={getEmptyDetailedComparisonData}
            name="detailedComparison"
            dateRange={dateRange}
          >
            {({ data }) => (
              <DetailedComparison
                showCents={shouldShowCents}
                data={data}
                isFetching={comparisonDataLoading || !defaultSelectedCreators}
                date={date}
              />
            )}
          </EditTableWrapper>
        )}

        {canSeeChatterLeaderboard && (
          <EditTableWrapper
            data={allChatters ?? []}
            getEmptyRow={getEmptyTrackingData}
            name="chatterLeaderboard"
            dateRange={dateRange}
          >
            {({ data }) => (
              <ChatterLeaderboardTable
                date={date}
                chatters={data}
                isFetching={chatterDataFetching}
              />
            )}
          </EditTableWrapper>
        )}
      </div>
    </Page>
  )
}
